import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import ModalShell from '../components/ModalShell';
import { ActionMenu, ActionMenuItem } from '../components/ActionMenu';
import { normalizeFieldText } from '../utils/textCase';
import { INVITE_EMAIL_BODY, INVITE_EMAIL_SUBJECT } from '../constants/portalBrand';
import MultiSelectFilter from '../components/MultiSelectFilter';
import QuickFilterGrid from '../components/QuickFilterGrid';
import ListFilterBar from '../components/ListFilterBar';
import FilterSearchField from '../components/FilterSearchField';
import { matchesOneOf } from '../utils/listFilters';
import SortableTh from '../components/SortableTh';
import { useSortableTable } from '../utils/sortableTable';
import { CLIENT_SORT_COLUMNS } from '../constants/listTableSort';
import TableExportButton from '../components/TableExportButton';
import { clientColumns, mapClients } from '../utils/dashboardColumns';
import { NATURE_OF_BUSINESS_OPTIONS } from '../constants/natureOfBusiness';

export default function Clients({
  embedded = false,
  embeddedEditId = null,
  initialClient = null,
  onEmbeddedClose = null
}) {
  const embeddedInitRef = useRef(false);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });
  const { data: jobs } = useQuery({ queryKey: ['jobs'], queryFn: () => api.get('/jobs').then(r => r.data) });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [paymentTermsFilters, setPaymentTermsFilters] = useState([]);
  const [locationFilters, setLocationFilters] = useState([]);
  const [jobsFilters, setJobsFilters] = useState([]);
  const [form, setForm] = useState({
    name: '',
    natureOfBusiness: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    paymentTerms: '',
    status: 'active',
    reapplyBlockMonths: 0,
    addresses: [{ label: 'Head office', address: '', contactPersonName: '', phone: '', email: '' }],
    password: '',
    sendInvite: false
  });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [addressEditor, setAddressEditor] = useState({ open: false, index: -1, draft: null });
  const [emailDraft, setEmailDraft] = useState({
    subject: INVITE_EMAIL_SUBJECT,
    body: INVITE_EMAIL_BODY
  });

  const openAddressEditor = (index = -1) => {
    const blank = { label: '', address: '', contactPersonName: '', phone: '', email: '' };
    const current = (form.addresses || [])[index] || blank;
    setAddressEditor({
      open: true,
      index,
      draft: { ...blank, ...current }
    });
  };

  const closeAddressEditor = () => {
    setAddressEditor({ open: false, index: -1, draft: null });
  };

  const saveAddressDraft = () => {
    const d = addressEditor.draft || { label: '', address: '', contactPersonName: '', phone: '', email: '' };
    const next = {
      label: String(d.label || '').trim(),
      address: String(d.address || '').trim(),
      contactPersonName: String(d.contactPersonName || '').trim(),
      phone: String(d.phone || '').trim(),
      email: String(d.email || '').trim()
    };
    if (!next.label && !next.address && !next.contactPersonName && !next.phone && !next.email) {
      closeAddressEditor();
      return;
    }
    setForm(f => {
      const list = [...(f.addresses || [])];
      if (addressEditor.index >= 0) {
        list[addressEditor.index] = next;
      } else {
        list.push(next);
      }
      return { ...f, addresses: list };
    });
    closeAddressEditor();
  };

  const removeAddress = index => {
    setForm(f => ({ ...f, addresses: (f.addresses || []).filter((_, i) => i !== index) }));
  };

  const createClient = useMutation({
    mutationFn: payload => api.post('/clients', {
      name: payload.name,
      natureOfBusiness: payload.natureOfBusiness,
      contacts: [{ name: payload.contactName, email: payload.contactEmail, phone: payload.contactPhone }],
      paymentTerms: payload.paymentTerms,
      status: payload.status,
      reapplyBlockMonths: payload.reapplyBlockMonths,
      addresses: payload.addresses,
      password: payload.password || 'password123',
      sendInvite: payload.sendInvite || false
    }).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      if (data.inviteSent && data.user) {
        alert(`Client created! Login credentials:\nEmail: ${data.user.email}\nPassword: ${data.user.password}\n\n(Email invite would be sent in production)`);
      }
      setForm({
        name: '',
        natureOfBusiness: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        paymentTerms: '',
        status: 'active',
        reapplyBlockMonths: 0,
        addresses: [{ label: 'Head office', address: '', contactPersonName: '', phone: '', email: '' }],
        password: '',
        sendInvite: false
      });
      setShowModal(false);
      setEditing(null);
      if (embedded && onEmbeddedClose) onEmbeddedClose();
    }
  });

  const updateClient = useMutation({
    mutationFn: client => api.put(`/clients/${client.id}`, client).then(r => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      if (variables.sendInvite && variables.password) {
        alert(
          `Client updated. Login credentials email sent to ${variables.contacts?.[0]?.email || 'contact'}.`
        );
      }
      setForm({
        name: '',
        natureOfBusiness: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        paymentTerms: '',
        status: 'active',
        reapplyBlockMonths: 0,
        addresses: [{ label: 'Head office', address: '', contactPersonName: '', phone: '', email: '' }],
        password: '',
        sendInvite: false
      });
      closeClientModal();
    }
  });

  const changePassword = useMutation({
    mutationFn: ({ id, password }) => api.put(`/clients/${id}`, { password }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      setShowPasswordModal(null);
      setNewPassword('');
      alert('Password changed successfully!');
    },
    onError: (error) => {
      console.error('Error changing password:', error);
      alert('Error changing password: ' + (error.response?.data?.message || error.message));
    }
  });

  const deleteClient = useMutation({
    mutationFn: id => api.delete(`/clients/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] })
  });

  const openModal = (client = null) => {
    setEditing(client);
    if (client) {
      setForm({
        name: client.name || '',
        natureOfBusiness: client.natureOfBusiness || '',
        contactName: client.contacts?.[0]?.name || '',
        contactEmail: client.contacts?.[0]?.email || '',
        contactPhone: client.contacts?.[0]?.phone || '',
        paymentTerms: client.paymentTerms || '',
        status: client.status || 'active',
        reapplyBlockMonths: client.reapplyBlockMonths || 0,
        addresses:
          (client.addresses && client.addresses.length
            ? client.addresses
            : [{ label: 'Head office', address: '', contactPersonName: '', phone: '', email: '' }]),
        password: '',
        sendInvite: false
      });
    } else {
      setForm({
        name: '',
        natureOfBusiness: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        paymentTerms: '',
        status: 'active',
        reapplyBlockMonths: 0,
        addresses: [{ label: 'Head office', address: '', contactPersonName: '', phone: '', email: '' }],
        password: 'password123',
        sendInvite: false
      });
    }
    setShowModal(true);
  };

  const closeClientModal = () => {
    setShowModal(false);
    setEditing(null);
    if (embedded && onEmbeddedClose) onEmbeddedClose();
  };

  useEffect(() => {
    if (!embedded || !embeddedEditId || embeddedInitRef.current) return;
    const client =
      initialClient?.id === embeddedEditId
        ? initialClient
        : data?.find(c => c.id === embeddedEditId);
    if (client) {
      embeddedInitRef.current = true;
      openModal(client);
    }
  }, [embedded, embeddedEditId, initialClient, data]);

  const submitForm = () => {
    if (form.sendInvite && !form.password?.trim()) {
      alert('Enter a password to include in the invite email.');
      return;
    }

    const payload = {
      name: form.name,
      natureOfBusiness: form.natureOfBusiness,
      contacts: [{ name: form.contactName, email: form.contactEmail, phone: form.contactPhone }],
      paymentTerms: form.paymentTerms,
      status: form.status,
      reapplyBlockMonths: form.reapplyBlockMonths,
      addresses: form.addresses,
      sendInvite: form.sendInvite || false
    };
    if (form.password) payload.password = form.password;

    if (editing) {
      updateClient.mutate({ ...editing, ...payload });
    } else {
      createClient.mutate({ ...form, ...payload, password: form.password || 'password123' });
    }
  };

  const jobCount = (clientId) => (jobs || []).filter(j => j.clientId === clientId).length;

  const filterOptions = useMemo(() => {
    const clients = data || [];
    const paymentSet = new Set();
    const locationSet = new Set();

    for (const client of clients) {
      if (client.paymentTerms?.trim()) paymentSet.add(client.paymentTerms.trim());
      for (const loc of client.locations || []) {
        if (loc?.trim()) locationSet.add(loc.trim());
      }
    }

    return {
      paymentTerms: [...paymentSet].sort((a, b) => a.localeCompare(b)),
      locations: [...locationSet].sort((a, b) => a.localeCompare(b))
    };
  }, [data]);

  const filteredClients = useMemo(() => {
    if (!data) return [];
    const q = searchQuery.trim().toLowerCase();
    return data.filter(client => {
      const status = (client.status || 'active').toLowerCase();
      const count = jobCount(client.id);

      if (!matchesOneOf(statusFilters, status)) return false;
      if (!matchesOneOf(paymentTermsFilters, (client.paymentTerms || '').trim())) return false;
      if (locationFilters.length) {
        const locs = (client.locations || []).map(l => (l || '').trim());
        if (!locationFilters.some(loc => locs.includes(loc))) return false;
      }
      if (jobsFilters.length) {
        const jobsOk = jobsFilters.some(f => {
          if (f === 'none') return count === 0;
          if (f === 'has') return count > 0;
          if (f === 'multi') return count >= 2;
          return false;
        });
        if (!jobsOk) return false;
      }

      if (!q) return true;
      const contactText = (client.contacts || [])
        .map(c => [c.name, c.email, c.phone].filter(Boolean).join(' '))
        .join(' ');
      const locationText = (client.locations || []).join(' ');
      const haystack = [
        client.id,
        client.name,
        client.paymentTerms,
        client.status,
        client.notes,
        contactText,
        locationText
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data, searchQuery, statusFilters, paymentTermsFilters, locationFilters, jobsFilters, jobs]);

  const clientSortColumns = useMemo(
    () => [
      ...CLIENT_SORT_COLUMNS,
      { key: 'jobs', type: 'number', getValue: c => jobCount(c.id) }
    ],
    [jobs]
  );

  const { sort, toggleSort, sortedRows: sortedClients } = useSortableTable(
    filteredClients,
    clientSortColumns
  );

  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
    statusFilters.length > 0 ||
    paymentTermsFilters.length > 0 ||
    locationFilters.length > 0 ||
    jobsFilters.length > 0
  );

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilters([]);
    setPaymentTermsFilters([]);
    setLocationFilters([]);
    setJobsFilters([]);
  };

  const statusFilterOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const jobsFilterOptions = [
    { value: 'has', label: 'Has jobs' },
    { value: 'none', label: 'No jobs' },
    { value: 'multi', label: 'Multiple jobs (2+)' }
  ];

  if (!embedded && isLoading) return <div>Loading...</div>;

  if (embedded && !showModal) {
    return (
      <div className="fixed inset-0 z-[14000] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl px-6 py-4 text-sm text-[hsl(var(--ds-fg))] shadow-xl">
          Loading client…
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'space-y-4'}>
      {!embedded && (
      <>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Clients</h1>
        <div className="flex items-center gap-2">
          <TableExportButton
            filename="clients"
            sheetName="Clients"
            columns={clientColumns}
            rows={mapClients(sortedClients)}
          />
          <button className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-2 text-sm font-medium" onClick={() => openModal()}>
            Add client
          </button>
        </div>
      </div>

      <ListFilterBar>
        <QuickFilterGrid
          search={
            <FilterSearchField
              placeholder="Search by name, ID, contact, payment terms..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
          }
          filters={[
            <MultiSelectFilter
              key="status"
              options={statusFilterOptions}
              values={statusFilters}
              onChange={setStatusFilters}
              emptyLabel="All statuses"
            />,
            <MultiSelectFilter
              key="payment"
              options={filterOptions.paymentTerms.map(term => ({ value: term, label: term }))}
              values={paymentTermsFilters}
              onChange={setPaymentTermsFilters}
              emptyLabel="All payment terms"
            />,
            <MultiSelectFilter
              key="location"
              options={filterOptions.locations.map(loc => ({ value: loc, label: loc }))}
              values={locationFilters}
              onChange={setLocationFilters}
              emptyLabel="All locations"
            />,
            <MultiSelectFilter
              key="jobs"
              options={jobsFilterOptions}
              values={jobsFilters}
              onChange={setJobsFilters}
              emptyLabel="All job counts"
            />
          ]}
        />
        {hasActiveFilters && (
          <div className="mt-2 flex items-center justify-between text-xs text-[hsl(var(--ds-muted-fg))]">
            <span>
              Showing {sortedClients.length} of {data?.length || 0} clients
            </span>
            <button type="button" className="text-[hsl(var(--ds-primary))] hover:underline" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </ListFilterBar>

      <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl shadow-sm overflow-hidden">
        <div className="max-h-[520px] overflow-y-auto">
          {(!data || data.length === 0) ? (
            <div className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">No clients found</div>
          ) : sortedClients.length === 0 ? (
            <div className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
              No clients match your search or filters
            </div>
          ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))] sticky top-0 z-10">
            <tr>
              <th className="text-left px-4 py-2">Sl No</th>
              <SortableTh label="Client ID" sortKey="id" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
              <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} className="px-4 py-2" />
              <SortableTh label="Contacts" sortKey="contacts" sort={sort} onSort={toggleSort} className="px-4 py-2" />
              <SortableTh label="Payment Terms" sortKey="paymentTerms" sort={sort} onSort={toggleSort} className="px-4 py-2" />
              <SortableTh label="Jobs" sortKey="jobs" sort={sort} onSort={toggleSort} className="px-4 py-2" />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} className="px-4 py-2" />
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedClients.map((client, idx) => (
              <tr key={client.id} className="border-b border-[hsl(var(--ds-border))]">
                <td className="px-4 py-2 text-xs text-[hsl(var(--ds-muted-fg))] whitespace-nowrap">{idx + 1}</td>
                <td className="px-4 py-2 text-xs text-[hsl(var(--ds-muted-fg))] whitespace-nowrap font-mono">{client.id}</td>
                <td className="px-4 py-2">
                  <button
                    type="button"
                    className="font-medium text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 text-left"
                    onClick={() => openModal(client)}
                  >
                    {client.name}
                  </button>
                </td>
                <td className="px-4 py-2">
                  {(client.contacts || []).map(c => (
                    <div key={c.email}>{c.name} ({c.email})</div>
                  ))}
                </td>
                <td className="px-4 py-2">{client.paymentTerms}</td>
                <td className="px-4 py-2">{jobCount(client.id)}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 text-xs ${client.status === 'inactive' ? 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]' : 'bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]'}`}>
                    {client.status || 'active'}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <ActionMenu>
                    <ActionMenuItem onClick={() => openModal(client)}>Edit</ActionMenuItem>
                    <ActionMenuItem onClick={() => setShowPasswordModal(client)}>Change Password</ActionMenuItem>
                    <ActionMenuItem onClick={() => updateClient.mutate({ ...client, status: 'inactive' })}>Deactivate</ActionMenuItem>
                    <ActionMenuItem danger onClick={() => { if (window.confirm('Are you sure you want to delete this client?')) deleteClient.mutate(client.id); }}>Delete</ActionMenuItem>
                  </ActionMenu>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
          )}
        </div>
      </div>
      </>
      )}

      {showModal && (
        <ModalShell
          title={editing ? 'Edit client' : 'Add client'}
          onClose={closeClientModal}
          maxWidth="max-w-[43rem]"
          zIndex={embedded ? 'z-[14000]' : undefined}
          curvy
          footer={
            <>
              {(createClient.isError || updateClient.isError) && (
                <span className="text-sm text-red-600 mr-auto">
                  {createClient.error?.response?.data?.message || updateClient.error?.response?.data?.message || 'Error saving'}
                </span>
              )}
              <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={closeClientModal}>Cancel</button>
              <button type="button" className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2 text-sm font-medium disabled:opacity-50" onClick={submitForm} disabled={createClient.isLoading || updateClient.isLoading}>
                {editing ? 'Save changes' : 'Add client'}
              </button>
            </>
          }
        >
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.name} onChange={e => setForm(f => ({ ...f, name: normalizeFieldText(e.target.value, 'name') }))} onBlur={e => setForm(f => ({ ...f, name: normalizeFieldText(e.target.value, 'name', { final: true }) }))} />
                <label>Client Name</label>
              </div>
              <div className="field-with-label">
                <select
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  value={form.natureOfBusiness || ''}
                  onChange={e => setForm(f => ({ ...f, natureOfBusiness: e.target.value }))}
                >
                  <option value="">Select nature of business</option>
                  {NATURE_OF_BUSINESS_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <label>Nature of business</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: normalizeFieldText(e.target.value, 'name') }))} />
                <label>Contact Name</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: normalizeFieldText(e.target.value, 'email') }))} />
                <label>Contact Email</label>
              </div>
              <div className="field-with-label">
                <input
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  type="password"
                  placeholder=" "
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                />
                <label>{editing ? 'New password' : 'Initial Password'}</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: normalizeFieldText(e.target.value, 'phone') }))} />
                <label>Contact Phone</label>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" placeholder=" " value={form.paymentTerms} onChange={e => setForm(f => ({ ...f, paymentTerms: e.target.value }))} />
                <label>Payment Terms</label>
              </div>
              <div className="field-with-label">
                <select className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <label>Status</label>
              </div>
              <div className="field-with-label">
                <input
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  type="number"
                  min={0}
                  placeholder=" "
                  value={form.reapplyBlockMonths}
                  onChange={e =>
                    setForm(f => ({
                      ...f,
                      reapplyBlockMonths: Math.max(0, Number(e.target.value) || 0)
                    }))
                  }
                />
                <label>Re-apply block after rejection (months)</label>
              </div>
              <p className="text-xs text-[hsl(var(--ds-muted-fg))] md:col-span-2 lg:col-span-3 -mt-1">
                If a candidate is rejected for any job under this client, they cannot be submitted/applied to this
                same client again until this cooldown ends. They can still apply to other clients.
              </p>

              <div className="md:col-span-2 lg:col-span-3 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Interview locations</p>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">
                      Saved locations can be selected when scheduling in-person interviews. Phone interviews auto-use the primary contact number.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="text-xs bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] px-3 py-2 hover:bg-[hsl(var(--ds-muted))]"
                    onClick={() => openAddressEditor(-1)}
                  >
                    Add new
                  </button>
                </div>

                <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] overflow-hidden">
                  {(form.addresses || []).length === 0 ? (
                    <div className="p-3 text-sm text-[hsl(var(--ds-muted-fg))]">No locations saved.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))]">
                          <tr>
                            <th className="text-left px-3 py-2 whitespace-nowrap">Sl No</th>
                            <th className="text-left px-3 py-2 whitespace-nowrap">Label</th>
                            <th className="text-left px-3 py-2 whitespace-nowrap">Contact person</th>
                            <th className="text-left px-3 py-2 whitespace-nowrap">Address</th>
                            <th className="text-left px-3 py-2 whitespace-nowrap">Phone</th>
                            <th className="text-left px-3 py-2 whitespace-nowrap">Email</th>
                            <th className="text-right px-3 py-2 whitespace-nowrap">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(form.addresses || []).map((addr, idx) => (
                            <tr key={idx} className="border-b border-[hsl(var(--ds-border))] align-top">
                              <td className="px-3 py-2 text-xs text-[hsl(var(--ds-muted-fg))] whitespace-nowrap">{idx + 1}</td>
                              <td className="px-3 py-2 font-medium text-[hsl(var(--ds-fg))] whitespace-nowrap">
                                {addr.label || `Location ${idx + 1}`}
                              </td>
                              <td className="px-3 py-2 text-[hsl(var(--ds-fg))] whitespace-nowrap">
                                {addr.contactPersonName || '—'}
                              </td>
                              <td className="px-3 py-2 text-[hsl(var(--ds-fg))] min-w-[16rem]">{addr.address || '—'}</td>
                              <td className="px-3 py-2 text-[hsl(var(--ds-fg))] whitespace-nowrap">{addr.phone || '—'}</td>
                              <td className="px-3 py-2 text-[hsl(var(--ds-fg))] whitespace-nowrap">{addr.email || '—'}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                <button
                                  type="button"
                                  className="text-xs text-[hsl(var(--ds-primary))] hover:underline mr-3"
                                  onClick={() => openAddressEditor(idx)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-red-600 hover:underline"
                                  onClick={() => removeAddress(idx)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              {editing && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] md:col-span-2 lg:col-span-3 -mt-1">
                  Leave blank to keep the current password. Fill in and check send invite to email updated credentials.
                </p>
              )}
              <div className="md:col-span-2 lg:col-span-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendInviteClient"
                  checked={form.sendInvite}
                  onChange={e => setForm(f => ({ ...f, sendInvite: e.target.checked }))}
                />
                <label htmlFor="sendInviteClient" className="text-sm">
                  Send email invite with login credentials
                </label>
              </div>
            </div>
            {form.sendInvite && (
              <div className="bg-[hsl(var(--ds-primary)/0.06)] border border-[hsl(var(--ds-primary)/0.25)] p-3 text-sm space-y-2">
                <div className="font-semibold">Email Draft (Editable):</div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium block mb-1">To:</label>
                    <input 
                      type="email" 
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs w-full" 
                      value={form.contactEmail} 
                      readOnly 
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Subject:</label>
                    <input 
                      type="text" 
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs w-full" 
                      value={emailDraft.subject} 
                      onChange={e => setEmailDraft(d => ({ ...d, subject: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Body:</label>
                    <textarea 
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs w-full" 
                      rows="8"
                      value={emailDraft.body.replace('[Name]', form.contactName || '[Name]').replace('[Email]', form.contactEmail || '[Email]').replace('[Password]', form.password || '[Password]').replace('[ApplicationURL]', window.location.origin)}
                      onChange={e => {
                        const newBody = e.target.value
                          .replace(form.contactName || '[Name]', '[Name]')
                          .replace(form.contactEmail || '[Email]', '[Email]')
                          .replace(form.password || '[Password]', '[Password]')
                          .replace(window.location.origin, '[ApplicationURL]');
                        setEmailDraft(d => ({ ...d, body: newBody }));
                      }}
                    />
                    <div className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                      Placeholders: [Name], [Email], [Password], [ApplicationURL]
                    </div>
                  </div>
                </div>
              </div>
            )}
        </ModalShell>
      )}

      {addressEditor.open && (
        <ModalShell
          title={addressEditor.index >= 0 ? 'Edit interview location' : 'Add interview location'}
          onClose={closeAddressEditor}
          maxWidth="max-w-lg"
          footer={
            <>
              <button
                type="button"
                className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]"
                onClick={closeAddressEditor}
              >
                Cancel
              </button>
              <button
                type="button"
                className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2 text-sm font-medium"
                onClick={saveAddressDraft}
              >
                Save location
              </button>
            </>
          }
        >
          <div className="grid gap-3 md:grid-cols-2">
            <div className="field-with-label">
              <input
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                placeholder=" "
                value={addressEditor.draft?.label || ''}
                onChange={e =>
                  setAddressEditor(s => ({ ...s, draft: { ...(s.draft || {}), label: e.target.value } }))
                }
              />
              <label>Label (e.g. Head office)</label>
            </div>
            <div className="field-with-label">
              <input
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                placeholder=" "
                value={addressEditor.draft?.contactPersonName || ''}
                onChange={e =>
                  setAddressEditor(s => ({ ...s, draft: { ...(s.draft || {}), contactPersonName: e.target.value } }))
                }
              />
              <label>Contact person name</label>
            </div>
            <div className="field-with-label">
              <input
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                placeholder=" "
                value={addressEditor.draft?.phone || ''}
                onChange={e =>
                  setAddressEditor(s => ({ ...s, draft: { ...(s.draft || {}), phone: e.target.value } }))
                }
              />
              <label>Contact phone</label>
            </div>
            <div className="field-with-label md:col-span-2">
              <input
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                placeholder=" "
                value={addressEditor.draft?.email || ''}
                onChange={e =>
                  setAddressEditor(s => ({ ...s, draft: { ...(s.draft || {}), email: e.target.value } }))
                }
              />
              <label>Contact email</label>
            </div>
            <div className="field-with-label md:col-span-2">
              <textarea
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                rows={3}
                placeholder=" "
                value={addressEditor.draft?.address || ''}
                onChange={e =>
                  setAddressEditor(s => ({ ...s, draft: { ...(s.draft || {}), address: e.target.value } }))
                }
              />
              <label>Address</label>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <ModalShell
          title="Change Password"
          subtitle={showPasswordModal.name}
          onClose={() => { setShowPasswordModal(null); setNewPassword(''); }}
          maxWidth="max-w-md"
          footer={
            <>
              <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={() => { setShowPasswordModal(null); setNewPassword(''); }}>Cancel</button>
              <button
                type="button"
                className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-4 py-2 text-sm font-medium disabled:opacity-60"
                onClick={() => changePassword.mutate({ id: showPasswordModal.id, password: newPassword })}
                disabled={!newPassword || changePassword.isLoading}
              >
                {changePassword.isLoading ? 'Changing...' : 'Change Password'}
              </button>
            </>
          }
        >
            <div className="space-y-3">
              <div className="text-sm text-[hsl(var(--ds-muted-fg))]">
                <div><strong>Client:</strong> {showPasswordModal.name}</div>
                <div><strong>Email:</strong> {showPasswordModal.contacts?.[0]?.email || '-'}</div>
              </div>
              <div className="field-with-label">
                <input className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]" type="password" placeholder=" " value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                <label>New Password</label>
              </div>
            </div>
        </ModalShell>
      )}
    </div>
  );
}

